import { useEffect, useState } from 'react';
import type { Contract } from '@forecastify/shared';
import { Dialog, Button, Select } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { contractLabel } from './contract-label';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Meglévő szerződés hozzáadása — C56.                                       │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The other half of the project tab's split button: put a contract that currently hangs off a
 * client onto this project.
 *
 * ⚠️⚠️ **THE CANDIDATES ARE PROJECT-LESS CONTRACTS, NARROWED TO THIS PROJECT'S CLIENT.** The
 * mockup's `cAttachCandidates` does exactly that, with an *any* escape hatch: a contract signed
 * with one client and delivered under another's project is a real arrangement, just an unusual
 * one, so the filter is a default rather than a rule.
 *
 * ⚠️ **The hint says the contract gains a code**, because that is the one surprising consequence.
 * Our attach mints one where the mockup's keeps an existing one — `contract.service.ts` carries
 * the argument, and it is worth saying on the screen rather than only in the source.
 */

export interface AttachContractDialogProps {
  readonly open: boolean;
  /** Every contract in the workspace; this narrows them itself. */
  readonly contracts: readonly Contract[];
  /** The project being attached TO — its client decides the default candidate list. */
  readonly projectClientId: string | null;
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (contract: Contract) => void;
}

export function AttachContractDialog({
  open,
  contracts,
  projectClientId,
  serverError,
  onClose,
  onSubmit,
}: AttachContractDialogProps): React.JSX.Element {
  const t = useT();
  const [chosen, setChosen] = useState('');
  const [anyClient, setAnyClient] = useState(false);

  /* ⚠️ Reset on open, or yesterday's pick is pre-selected in a dialog somebody just opened. */
  useEffect(() => {
    if (!open) return;
    setChosen('');
    setAnyClient(false);
  }, [open]);

  const free = contracts.filter((contract) => contract.projectId === null);
  const candidates = anyClient
    ? free
    : free.filter((contract) => projectClientId === null || contract.clientId === projectClientId);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('contract.attach.title')}
      description={t('contract.attach.hint')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={chosen === ''}
            onClick={() => {
              const contract = candidates.find((candidate) => candidate.id === chosen);
              if (contract !== undefined) onSubmit(contract);
            }}
          >
            {t('contract.attach.submit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {serverError !== null && (
          <p
            role="alert"
            className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-[12.5px]"
          >
            {serverError}
          </p>
        )}

        {candidates.length === 0 ? (
          <p className="text-muted-foreground text-[12.5px]">{t('contract.attach.none')}</p>
        ) : (
          <Field required label={t('contract.dialog.new')}>
            <Select
              /* U-14 · a list of records, so it searches. */
              searchable
              searchPlaceholder={t('list.searchOptions')}
              value={chosen}
              onChange={(event) => {
                setChosen(event.target.value);
              }}
              options={[
                { value: '', label: t('common.choose') },
                ...candidates.map((contract) => ({
                  value: contract.id,
                  /* ⚠️ The identifier IS the label here — these all have no code yet. */
                  label: `${contractLabel(contract)} · ${contract.name}`,
                })),
              ]}
            />
          </Field>
        )}

        {/**
         * ⚠️ **The escape hatch, and it is a checkbox rather than a second picker.** `cAttachSave`
         * offers *any* client; widening the list is a rarer decision than making the pick, so it
         * sits under it and off by default.
         */}
        {projectClientId !== null && free.length > candidates.length && (
          <label className="text-muted-foreground flex items-center gap-2 text-[11.5px]">
            <input
              type="checkbox"
              checked={anyClient}
              onChange={(event) => {
                setAnyClient(event.target.checked);
                setChosen('');
              }}
            />
            {t('contract.attach.anyClient')}
          </label>
        )}
      </div>
    </Dialog>
  );
}
